import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { FlightDeal } from '../types'
import { sortBy, uniqueBy } from 'lib/arrayUtils'
import { mediaQueryUp } from 'components/utils/breakpoint'
import GeoContext from 'contexts/geoContext'

import FlightDealConditions from './FlightDealConditions'
import FlightDealsList from './FlightDealsList'
import LocationSelect from 'components/Flights/FlightMerchandising/LocationSelect/LocationSelect'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'components/Luxkit/Typography/Heading'

const Root = styled(LayoutContainer)`
  > * + * {
    margin-top: ${rem(28)};
  }
`

const Header = styled(Heading)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  ${mediaQueryUp.sm} {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
`

const LocationDropdown = styled(LocationSelect)`
  margin-top: ${rem(8)};
  margin-left: 0;

  ${mediaQueryUp.sm} {
    margin-top: 0;
    margin-left: ${rem(12)};
  }
`

interface Props {
  flightDeals: Array<FlightDeal>;
}

function FlightDealsContainer({ flightDeals }: Props) {
  const locations = useMemo(() => {
    const uniqueLocations = uniqueBy(flightDeals, deal => deal.originAirportCode).map(deal => ({
      code: deal.originAirportCode,
      name: deal.originAirportName,
    }))

    return sortBy(uniqueLocations, ({ name }) => name, 'asc')
  }, [flightDeals])

  const { closestAirport } = useContext(GeoContext)
  const [deals, setDeals] = useState([])
  const defaultLocation = useMemo(() => locations.find(location => location.code === closestAirport.code), [locations, closestAirport])
  const [selectedLocation, setLocation] = useState(defaultLocation ?? locations[0])

  const onChangeLocation = useCallback((location: App.AllAirport) => {
    setLocation(location)
  }, [setLocation])

  useEffect(() => {
    const filteredDeals = flightDeals.filter(({ originAirportCode }) => originAirportCode === selectedLocation.code)
    setDeals(filteredDeals)
  }, [flightDeals, selectedLocation, setDeals])

  return (
    <Root>
      <Header variant="heading3">
        {`Explore the best flight deals ${locations.length > 1 ? ' departing from' : ''}`}
        {locations.length > 1 &&
          <LocationDropdown
            selected={selectedLocation}
            locations={locations}
            onChangeLocation={onChangeLocation}
          />
        }
      </Header>
      <FlightDealsList flightDeals={deals} />
      <FlightDealConditions />
    </Root>
  )
}

export default FlightDealsContainer
